<template>
  <!--
    WHAT — Structured Analysis detail tab "Output". Edits three JSON-blob config
      fields — time_window, output_schema, and display_schema — each in its own
      monospace textarea.

    CONTRACT — hydrates each textarea from JSON.stringify(record.config.<key>).
      On input for a field, PARSES that field and emits patch({ config }) where
      config is a spread of record.config with just that one key replaced by the
      parsed object. Mutates the ONE shared `config` blob like the other
      Structured* tabs.

    NOTES — hydrating guard: `text` seeded in data(), emit() no-ops until
      $nextTick(mounted) clears `hydrating`. Per-field JSON validation: invalid
      JSON sets errors[key] and RETURNS WITHOUT EMITTING, blocking ONLY that
      field's save (other fields still emit). selfChrome: true — renders its own
      titled card; ArtifactDetail adds no chrome. The single-patient test harness
      lives in its own "Test" tab (StructuredTestSection.vue), not here.
  -->
  <!-- Structured Analysis "Output" — time window, output schema, display schema
       (each a JSON blob). Self-chromed: renders its own titled cards. Patches
       config.{time_window,output_schema,display_schema}. Invalid JSON blocks
       only its own field's emit. -->
  <section class="bg-white border border-gray-200 rounded-xl overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100">
      <span class="text-[13px] font-semibold text-gray-800">Output</span>
    </div>
    <div class="p-5 space-y-5">
      <div v-for="f in visibleFields" :key="f.key">
        <div class="flex items-center justify-between mb-1">
          <label class="block text-[11px] font-medium text-gray-600">{{ f.label }}</label>
          <span v-if="errors[f.key]" class="text-[10px] text-red-500">{{ errors[f.key] }}</span>
        </div>
        <textarea
          v-model="text[f.key]"
          :rows="f.rows"
          spellcheck="false"
          class="w-full text-[11px] px-2 py-1.5 border rounded-lg font-mono bg-white focus:outline-none resize-y leading-relaxed"
          :class="errors[f.key] ? 'border-red-400' : 'border-gray-200 focus:border-blue-400'"
          @input="emit(f.key)"
        ></textarea>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'StructuredOutputSection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    const cfg = this.record.config || {};
    const fields = [
      { key: 'time_window', label: 'Time window', rows: 3, cfgKey: 'time_window' },
      { key: 'output_schema', label: 'Output schema', rows: 12, cfgKey: 'output_schema' },
      { key: 'display_schema', label: 'Display schema', rows: 5, cfgKey: 'display_schema' },
    ];
    const text = {};
    fields.forEach((f) => { text[f.key] = JSON.stringify(cfg[f.cfgKey] || {}, null, 2); });
    return {
      fields, text, errors: { time_window: null, output_schema: null, display_schema: null }, hydrating: true,
    };
  },
  computed: {
    isDossier() {
      return ((this.record.config || {}).executor) === 'patient_dossier';
    },
    // time_window is an agent-executor scoping concept; the dossier executor
    // has no time window (it reads the patient's whole subgraph), so hide it.
    visibleFields() {
      return this.isDossier ? this.fields.filter((f) => f.key !== 'time_window') : this.fields;
    },
  },
  mounted() { this.$nextTick(() => { this.hydrating = false; }); },
  methods: {
    emit(key) {
      if (this.hydrating) return;
      let parsed;
      try { parsed = JSON.parse(this.text[key] || '{}'); this.errors[key] = null; }
      catch (e) { this.errors[key] = `Invalid JSON: ${e.message}`; return; } // don't emit while invalid
      const config = { ...(this.record.config || {}), [key]: parsed };
      this.$emit('patch', { config });
    },
  },
};
</script>
